/**
 * The stages a default save goes to (the Stage row of the Defaults panel).
 *
 * A default-scene row is keyed by its stage (`type`), and the cascade reads
 * the row of the stage on screen only. So "the same default for Beta and
 * Release" is one write per stage. The panel writes the stages an admin
 * checks; the stage on screen is checked at the start.
 *
 * Each stage has its own background gallery: a background number names
 * another image in another stage. The fields that name or fit one background
 * of the gallery therefore save only for the stage on screen, and so do the
 * rows keyed by a background.
 */
import {findChannelObj} from "data/products";
import {BG_PREVIEW_TEMPLATE_FIELD} from "libs/bgPreviewTemplate";
import {stagesWithProduct} from "libs/productOffer";
import type {ChannelSettingsLike} from "libs/selectorVisibility";
import {applyStageOrder} from "libs/stageOrder";
import {CROP_FIELDS} from "./cropRowTarget";
import {BG_COLOUR_FIELDS} from "./lookRowTarget";
import {channelStageOptions} from "./StageOrderSection";

export interface StageOption {
  label: string;
  value: string;
}

/**
 * Which background opens, its crop and preview banner, its brightness, blur
 * and opacity, the colours of the banners on it (base, text, logos), the
 * custom image and the banner images saved under it, and the images cut from
 * them.
 */
export const BACKGROUND_BOUND_FIELDS: readonly string[] = Object.freeze([
  "bgOption",
  "allProductsBgOptions",
  ...CROP_FIELDS,
  BG_PREVIEW_TEMPLATE_FIELD,
  "bgBrightness",
  "bgBlurValue",
  "bgOpacity",
  "bgBrightnesses",
  "bgOpacities",
  "customBg",
  "customBgs",
  "bakedBgs",
  "bakedBgMeta",
  ...BG_COLOUR_FIELDS,
]);

const BACKGROUND_BOUND = new Set(BACKGROUND_BOUND_FIELDS);

/** The levels whose row is keyed by a background of the gallery. */
const BACKGROUND_LEVELS = new Set(["background", "layout-background"]);

export function isBackgroundLevel(level: string): boolean {
  return BACKGROUND_LEVELS.has(level);
}

/**
 * The stages a default can be saved for, in the saved Stage order: the
 * product's own (the code's and the ones an admin put it on), or every stage
 * of the channel for the "All products" scope (`product` "*"). A channel
 * whose types are not release stages has none.
 */
export function defaultStageOptions({
  channel,
  product,
  settings,
}: {
  channel: string;
  product: string;
  /** The channel's settings: the saved Stage order and the stage rules. */
  settings: ChannelSettingsLike & {stageOrder?: string[] | null};
}): StageOption[] {
  if (!findChannelObj(channel)?.typesAreStages) return [];
  const stages = channelStageOptions(channel);
  const productStages = stagesWithProduct(settings, channel, product, {selectable: true});
  const offered =
    product === "*" ? stages : stages.filter((stage) => productStages.includes(stage.value));
  return applyStageOrder(offered, settings?.stageOrder);
}

/**
 * The stages a save writes: the checked ones that the scope offers, in the
 * options' order. With none of them, the stage on screen.
 */
export function resolveTargetStages(
  checked: readonly string[],
  current: string,
  options: readonly StageOption[]
): string[] {
  const targets = options
    .filter((option) => checked.includes(option.value))
    .map((option) => option.value);
  return targets.length > 0 ? targets : [current];
}

/** `sceneData` without the fields bound to one background of the gallery. */
export function withoutBackgroundBoundFields(
  sceneData: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(sceneData)) {
    if (!BACKGROUND_BOUND.has(field)) result[field] = value;
  }
  return result;
}
